import { create } from 'zustand';
import type { Repository, Chart, Session, RenderResult, EditorTab, ChartDetails } from '../types';

interface AppState {
  // Repositories
  repositories: Repository[];
  selectedRepo: string | null;
  setRepositories: (repos: Repository[]) => void;
  setSelectedRepo: (repo: string | null) => void;

  // Charts
  charts: Record<string, Chart[]>;
  selectedChart: Chart | null;
  chartDetails: ChartDetails | null;
  setCharts: (charts: Record<string, Chart[]>) => void;
  setSelectedChart: (chart: Chart | null) => void;
  setChartDetails: (details: ChartDetails | null) => void;

  // Session
  session: Session | null;
  setSession: (session: Session | null) => void;

  // Editor
  values: string;
  templates: Record<string, string>;
  activeTab: string;
  editorTabs: EditorTab[];
  setValues: (values: string) => void;
  setTemplates: (templates: Record<string, string>) => void;
  updateTemplate: (name: string, content: string) => void;
  setActiveTab: (tabId: string) => void;
  setEditorTabs: (tabs: EditorTab[]) => void;
  addEditorTab: (tab: EditorTab) => void;
  removeEditorTab: (tabId: string) => void;

  // Render
  renderResult: RenderResult | null;
  isRendering: boolean;
  setRenderResult: (result: RenderResult | null) => void;
  setIsRendering: (isRendering: boolean) => void;

  // UI
  sidebarOpen: boolean;
  outputPanelOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  setOutputPanelOpen: (open: boolean) => void;

  // Reset
  resetEditor: () => void;
}

export const useStore = create<AppState>((set) => ({
  // Repositories
  repositories: [],
  selectedRepo: null,
  setRepositories: (repos) => set({ repositories: repos }),
  setSelectedRepo: (repo) => set({ selectedRepo: repo }),

  // Charts
  charts: {},
  selectedChart: null,
  chartDetails: null,
  setCharts: (charts) => set({ charts }),
  setSelectedChart: (chart) => set({ selectedChart: chart }),
  setChartDetails: (details) => set({ chartDetails: details }),

  // Session
  session: null,
  setSession: (session) => set({ session }),

  // Editor
  values: '',
  templates: {},
  activeTab: 'values',
  editorTabs: [
    { id: 'values', name: 'values.yaml', type: 'values', content: '', language: 'yaml' },
  ],
  setValues: (values) => set({ values }),
  setTemplates: (templates) => set({ templates }),
  updateTemplate: (name, content) => set((state) => ({
    templates: { ...state.templates, [name]: content },
  })),
  setActiveTab: (tabId) => set({ activeTab: tabId }),
  setEditorTabs: (tabs) => set({ editorTabs: tabs }),
  addEditorTab: (tab) => set((state) => {
    if (state.editorTabs.find((t) => t.id === tab.id)) {
      return { activeTab: tab.id };
    }
    return {
      editorTabs: [...state.editorTabs, tab],
      activeTab: tab.id,
    };
  }),
  removeEditorTab: (tabId) => set((state) => {
    const tabs = state.editorTabs.filter((t) => t.id !== tabId);
    const activeTab = state.activeTab === tabId
      ? tabs[tabs.length - 1]?.id || 'values'
      : state.activeTab;
    return { editorTabs: tabs, activeTab };
  }),

  // Render
  renderResult: null,
  isRendering: false,
  setRenderResult: (result) => set({ renderResult: result }),
  setIsRendering: (isRendering) => set({ isRendering }),

  // UI
  sidebarOpen: true,
  outputPanelOpen: true,
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  setOutputPanelOpen: (open) => set({ outputPanelOpen: open }),

  // Reset
  resetEditor: () => {
    set({
      session: null,
      selectedChart: null,
      chartDetails: null,
      values: '',
      templates: {},
      renderResult: null,
      editorTabs: [
        { id: 'values', name: 'values.yaml', type: 'values', content: '', language: 'yaml' },
      ],
      activeTab: 'values',
    });
  },
}));
